import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { InvoiceCustomStyle, InvoiceFontFamily, InvoiceIconName } from "../types/invoiceCustomStyle.types";
import { defaultInvoiceCustomStyle } from "../constants/defaultInvoiceCustomStyle";
import type { SvgIconComponent } from "@mui/icons-material";
import { invoiceStylePresets, type InvoiceStylePresetName } from "../constants/invoiceStylePresets";
import {
  AssignmentRounded,
  CalendarMonthRounded,
  CleaningServicesRounded,
  DescriptionRounded,
  ExpandMoreRounded,
  LocationOnRounded,
  PaymentsRounded,
} from "@mui/icons-material";
import { getContrastRatio } from "@mui/material/styles";
import { useMemo } from "react";
import { InvoiceColorField } from "./InvoiceColorField";
import { InvoicePresetCards } from "./InvoicePresetCards";

interface InvoiceCustomizationPanelProps {
  value: InvoiceCustomStyle;
  onChange: (value: InvoiceCustomStyle) => void;
  isDirty?: boolean;
  isSaving?: boolean;
  onSave?: () => void;
  onDiscard?: () => void;
}

const fontOptions: InvoiceFontFamily[] = ["Inter", "Roboto", "Lato", "Montserrat", "Merriweather", "Source Sans 3"];

const iconOptions: Array<{
  value: InvoiceIconName;
  label: string;
}> = [
  { value: "default", label: "Default section icon" },
  { value: "cleaning", label: "Cleaning" },
  { value: "calendar", label: "Calendar" },
  { value: "document", label: "Document" },
  { value: "location", label: "Location" },
  { value: "assignment", label: "Assignment" },
  { value: "payment", label: "Payment" },
  { value: "none", label: "None" },
];

const iconPreviewRegistry: Partial<Record<InvoiceIconName, SvgIconComponent>> = {
  cleaning: CleaningServicesRounded,
  calendar: CalendarMonthRounded,
  document: DescriptionRounded,
  location: LocationOnRounded,
  assignment: AssignmentRounded,
  payment: PaymentsRounded,
};

const presetNames = Object.keys(invoiceStylePresets) as InvoiceStylePresetName[];

const safeContrast = (foreground: string, background: string) => {
  try {
    return getContrastRatio(foreground, background);
  } catch {
    return 21;
  }
};

const groupCaptionSx = {
  fontSize: "0.6875rem",
  fontWeight: 800,
  letterSpacing: "0.07em",
  textTransform: "uppercase" as const,
  color: "text.secondary",
};

export const InvoiceCustomizationPanel = ({ value, onChange, isDirty, isSaving, onSave, onDiscard }: InvoiceCustomizationPanelProps) => {
  const selectedPreset = useMemo(() => {
    const serialized = JSON.stringify(value);

    return presetNames.find((name) => JSON.stringify(invoiceStylePresets[name]) === serialized) ?? null;
  }, [value]);

  const applyPreset = (preset: InvoiceStylePresetName) => onChange(structuredClone(invoiceStylePresets[preset]));

  const updateColor = (key: keyof InvoiceCustomStyle["colors"], color: string) => {
    onChange({
      ...value,
      colors: {
        ...value.colors,
        [key]: color,
      },
    });
  };

  const updateTypography = (key: keyof InvoiceCustomStyle["typography"], fontFamily: InvoiceFontFamily) => {
    onChange({
      ...value,
      typography: {
        ...value.typography,
        [key]: fontFamily,
      },
    });
  };

  const updateBorder = <K extends keyof InvoiceCustomStyle["borders"]>(key: K, newValue: InvoiceCustomStyle["borders"][K]) => {
    onChange({
      ...value,
      borders: {
        ...value.borders,
        [key]: newValue,
      },
    });
  };

  const updateSpacing = <K extends keyof InvoiceCustomStyle["spacing"]>(key: K, newValue: InvoiceCustomStyle["spacing"][K]) => {
    onChange({
      ...value,
      spacing: {
        ...value.spacing,
        [key]: newValue,
      },
    });
  };

  const updateIcon = (key: keyof InvoiceCustomStyle["icons"], icon: InvoiceIconName) => {
    onChange({
      ...value,
      icons: {
        ...value.icons,
        [key]: icon,
      },
    });
  };

  const updateLabel = (key: keyof NonNullable<InvoiceCustomStyle["labels"]>, labelValue: string) => {
    onChange({
      ...value,
      labels: {
        ...value.labels,
        [key]: labelValue,
      },
    });
  };

  const handleReset = () => onChange(structuredClone(defaultInvoiceCustomStyle));

  const contrastWarning = (color: string, background: string, minimum: number, message: string) =>
    safeContrast(color, background) < minimum ? message : undefined;

  return (
    <Box
      sx={{
        width: "99%",
        p: 2,
        borderRadius: 2,
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" fontWeight={900}>
            Invoice customization
          </Typography>

          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Styles apply to the client link, PDF and invoice email.
          </Typography>
        </Box>

        {isDirty ? <Chip size="small" color="warning" label="Unsaved" sx={{ fontWeight: 800, flexShrink: 0 }} /> : null}
      </Box>

      <Divider sx={{ my: 1 }} />

      <Typography sx={{ ...groupCaptionSx, mb: 1 }}>Presets</Typography>

      <InvoicePresetCards selected={selectedPreset} onSelect={applyPreset} />

      <Divider sx={{ my: 1 }} />

      <PanelSection title="Colors">
        <Stack spacing={2}>
          <Stack spacing={1.25}>
            <Typography sx={groupCaptionSx}>Highlights</Typography>

            <InvoiceColorField
              label="Total"
              helper="Amount due figure"
              value={value.colors.total}
              onChange={(color) => updateColor("total", color)}
            />

            <InvoiceColorField label="Icons" helper="Section icons" value={value.colors.icon} onChange={(color) => updateColor("icon", color)} />
          </Stack>

          <Stack spacing={1.25}>
            <Typography sx={groupCaptionSx}>Surfaces</Typography>

            <InvoiceColorField
              label="Page background"
              value={value.colors.pageBackground}
              warning={contrastWarning(
                value.colors.cardBackground,
                value.colors.pageBackground,
                1.1,
                "The card is nearly invisible against the page background.",
              )}
              onChange={(color) => updateColor("pageBackground", color)}
            />

            <InvoiceColorField
              label="Card background"
              value={value.colors.cardBackground}
              onChange={(color) => updateColor("cardBackground", color)}
            />

            <InvoiceColorField
              label="Divider"
              helper="Lines between sections"
              value={value.colors.border}
              onChange={(color) => updateColor("border", color)}
            />
          </Stack>

          <Stack spacing={1.25}>
            <Typography sx={groupCaptionSx}>Text</Typography>

            <InvoiceColorField
              label="Titles"
              helper="Invoice, Bill to, Service and Payment Details"
              value={value.colors.title}
              warning={contrastWarning(
                value.colors.title,
                value.colors.cardBackground,
                3,
                "Important titles may be hard to read against the card background.",
              )}
              onChange={(color) => updateColor("title", color)}
            />

            <InvoiceColorField
              label="Secondary text"
              helper="Service names, labels and secondary headings"
              value={value.colors.subTitle}
              warning={contrastWarning(
                value.colors.subTitle,
                value.colors.cardBackground,
                3,
                "Subtitles may be hard to read against the card background.",
              )}
              onChange={(color) => updateColor("subTitle", color)}
            />

            <InvoiceColorField
              label="Body"
              helper="Regular text and descriptions"
              value={value.colors.body}
              warning={contrastWarning(
                value.colors.body,
                value.colors.cardBackground,
                4.5,
                "Body text has low contrast against the card background.",
              )}
              onChange={(color) => updateColor("body", color)}
            />
          </Stack>
        </Stack>
      </PanelSection>

      <PanelSection title="Typography">
        <Stack spacing={1.5}>
          <TextField
            select
            size="small"
            label="Body font"
            value={value.typography.fontFamily}
            onChange={(event) => updateTypography("fontFamily", event.target.value as InvoiceFontFamily)}
          >
            {fontOptions.map((font) => (
              <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                {font}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Heading font"
            value={value.typography.headingFontFamily}
            onChange={(event) => updateTypography("headingFontFamily", event.target.value as InvoiceFontFamily)}
          >
            {fontOptions.map((font) => (
              <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                {font}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </PanelSection>

      <PanelSection title="Borders">
        <Stack spacing={2}>
          <InvoiceColorField
            label="Border color"
            helper="Card outline"
            value={value.borders.color}
            disabled={value.borders.width === 0}
            onChange={(color) => updateBorder("color", color)}
          />

          <TextField
            select
            size="small"
            label="Border style"
            value={value.borders.style}
            onChange={(event) => updateBorder("style", event.target.value as InvoiceCustomStyle["borders"]["style"])}
          >
            <MenuItem value="solid">Solid</MenuItem>
            <MenuItem value="dashed">Dashed</MenuItem>
          </TextField>

          <Box>
            <Typography variant="caption" fontWeight={700}>
              Border width: {value.borders.width}px
            </Typography>

            <Slider
              min={0}
              max={4}
              step={1}
              value={value.borders.width}
              valueLabelDisplay="auto"
              onChange={(_, newValue) => updateBorder("width", newValue as number)}
            />
          </Box>

          <Box>
            <Typography variant="caption" fontWeight={700}>
              Border radius: {value.borders.radius}px
            </Typography>

            <Slider
              min={0}
              max={32}
              step={1}
              value={value.borders.radius}
              valueLabelDisplay="auto"
              onChange={(_, newValue) => updateBorder("radius", newValue as number)}
            />
          </Box>
        </Stack>
      </PanelSection>

      <PanelSection title="Spacing">
        <Box>
          <Typography variant="caption" fontWeight={700}>
            Card gap: {value.spacing.cardGap}px
          </Typography>

          <Slider
            min={0}
            max={32}
            step={1}
            value={value.spacing.cardGap}
            valueLabelDisplay="auto"
            onChange={(_, newValue) => updateSpacing("cardGap", newValue as number)}
          />
        </Box>
      </PanelSection>

      <PanelSection title="Icons">
        <Stack spacing={1.5}>
          <IconSelect label="Service icon" value={value.icons.service} onChange={(icon) => updateIcon("service", icon)} />
          <IconSelect label="Notes icon" value={value.icons.notes} onChange={(icon) => updateIcon("notes", icon)} />
          <IconSelect label="Payment icon" value={value.icons.payment} onChange={(icon) => updateIcon("payment", icon)} />
          <IconSelect
            label="Additional charges icon"
            value={value.icons.additionalCharges}
            onChange={(icon) => updateIcon("additionalCharges", icon)}
          />
        </Stack>
      </PanelSection>

      <PanelSection title="Labels">
        <Stack spacing={1.5}>
          <TextField
            size="small"
            label="Service fee / Extra cost label"
            placeholder="e.g. Service fee, Extra cost, Taxes"
            helperText="Applies globally to all invoices when saved"
            value={value.labels?.increasePercentageByFrequency ?? ""}
            onChange={(event) => updateLabel("increasePercentageByFrequency", event.target.value)}
          />
        </Stack>
      </PanelSection>

      <Divider sx={{ my: 1 }} />

      <Stack spacing={1}>
        {onSave ? (
          <>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              disabled={!isDirty || isSaving}
              onClick={onSave}
              sx={{
                textTransform: "none",
                fontWeight: 800,
              }}
            >
              {isSaving ? "Saving…" : "Save for all invoices"}
            </Button>

            <Typography variant="caption" color="text.secondary">
              Saved styles apply to every invoice this company sends, including the client link, downloaded PDF and invoice email.
            </Typography>
          </>
        ) : null}

        {onDiscard ? (
          <Button
            fullWidth
            variant="text"
            disabled={!isDirty || isSaving}
            onClick={onDiscard}
            sx={{
              textTransform: "none",
              fontWeight: 800,
            }}
          >
            Discard changes
          </Button>
        ) : null}

        <Button
          fullWidth
          variant="outlined"
          onClick={handleReset}
          sx={{
            textTransform: "none",
            fontWeight: 800,
          }}
        >
          Reset to defaults
        </Button>
      </Stack>
    </Box>
  );
};

interface IconSelectProps {
  label: string;
  value: InvoiceIconName;
  onChange: (value: InvoiceIconName) => void;
}

const IconSelect = ({ label, value, onChange }: IconSelectProps) => {
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value as InvoiceIconName)}
      SelectProps={{
        renderValue: (selected) => {
          const selectedValue = selected as InvoiceIconName;
          const selectedOption = iconOptions.find((option) => option.value === selectedValue);
          const PreviewIcon = iconPreviewRegistry[selectedValue];

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {PreviewIcon ? (
                <PreviewIcon sx={{ fontSize: 18 }} />
              ) : (
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    color: "text.secondary",
                  }}
                >
                  —
                </Box>
              )}

              <Typography variant="body2">{selectedOption?.label ?? selectedValue}</Typography>
            </Box>
          );
        },
      }}
    >
      {iconOptions.map((option) => {
        const PreviewIcon = iconPreviewRegistry[option.value];

        return (
          <MenuItem key={option.value} value={option.value}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {PreviewIcon ? (
                <PreviewIcon sx={{ fontSize: 18 }} />
              ) : (
                <Box
                  sx={{
                    width: 18,
                    textAlign: "center",
                    color: "text.secondary",
                  }}
                >
                  —
                </Box>
              )}

              <Typography variant="body2">{option.label}</Typography>
            </Box>
          </MenuItem>
        );
      })}
    </TextField>
  );
};

interface PanelSectionProps {
  title: string;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

const PanelSection = ({ title, defaultExpanded = false, children }: PanelSectionProps) => (
  <Accordion
    disableGutters
    elevation={0}
    defaultExpanded={defaultExpanded}
    sx={{
      border: "1px solid var(--fx-surface-border, #E2E8F0)",
      borderRadius: "10px !important",
      overflow: "hidden",
      "&::before": {
        display: "none",
      },
      "& + &": {
        mt: 1,
      },
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreRounded />}
      sx={{
        minHeight: 38,
        px: 1.5,
        "& .MuiAccordionSummary-content": {
          my: 0,
        },
      }}
    >
      <Typography variant="subtitle2" fontWeight={800}>
        {title}
      </Typography>
    </AccordionSummary>

    <AccordionDetails
      sx={{
        px: 1.5,
        pt: 0.5,
        pb: 1.5,
      }}
    >
      {children}
    </AccordionDetails>
  </Accordion>
);
